import { PersonDto, RelationshipDto } from '../services/api';

export const normalizeRelationshipType = (relationshipType?: string): string =>
  relationshipType?.trim().replace(/[\s_-]/g, '').toLowerCase() ?? 'other';

export const getPersonDisplayName = (person?: Pick<PersonDto, 'firstName' | 'lastName' | 'fullName'> | null): string => {
  if (!person) {
    return 'Unknown person';
  }

  if (person.fullName?.trim()) {
    return person.fullName.trim();
  }

  const combinedName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return combinedName || 'Unknown person';
};

export const getParentLabelForGender = (gender?: string): 'Father' | 'Mother' | 'Parent' => {
  switch (gender?.trim().toLowerCase()) {
    case 'male':
      return 'Father';
    case 'female':
      return 'Mother';
    default:
      return 'Parent';
  }
};

export const toDisplayRelationshipLabel = (relationshipType?: string): string => {
  const normalized = normalizeRelationshipType(relationshipType);

  switch (normalized) {
    case 'inlaw':
      return 'In-Law';
    case 'stepfather':
      return 'Step Father';
    case 'stepmother':
      return 'Step Mother';
    case 'stepsibling':
      return 'Step-Sibling';
    case 'other':
      return 'Related';
    default:
      return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Related';
  }
};

export const getDisplayRelationshipLabelForEdge = (
  relationshipType: string | undefined,
  personB?: Pick<PersonDto, 'gender'> | null
): string => {
  const normalized = normalizeRelationshipType(relationshipType);

  if (normalized === 'parent') {
    return getParentLabelForGender(personB?.gender);
  }

  return toDisplayRelationshipLabel(relationshipType);
};

export const getRelativeLabelForPerson = (
  relationship: RelationshipDto,
  currentPersonId: string,
  relatedPerson?: Pick<PersonDto, 'gender'> | null
): string => {
  const normalized = normalizeRelationshipType(relationship.type);

  if (relationship.personAId === currentPersonId) {
    if (normalized === 'parent') {
      return getParentLabelForGender(relatedPerson?.gender);
    }

    return toDisplayRelationshipLabel(relationship.type);
  }

  const inverseRelationshipMap: Record<string, string> = {
    parent: 'Child',
    stepfather: 'Step-Child',
    stepmother: 'Step-Child',
    child: getParentLabelForGender(relatedPerson?.gender),
    grandparent: 'Grandchild',
    grandchild: 'Grandparent',
    aunt: 'Niece/Nephew',
    uncle: 'Niece/Nephew',
    spouse: 'Spouse',
    sibling: 'Sibling',
    stepsibling: 'Step-Sibling',
    cousin: 'Cousin',
    inlaw: 'In-Law',
    other: 'Related',
  };

  return inverseRelationshipMap[normalized] ?? toDisplayRelationshipLabel(relationship.type);
};

export const getRelationshipPreview = (
  personA: Pick<PersonDto, 'firstName' | 'lastName' | 'fullName'> | null | undefined,
  relationshipType: string,
  personB: Pick<PersonDto, 'firstName' | 'lastName' | 'fullName' | 'gender'> | null | undefined
): string => {
  const personAName = getPersonDisplayName(personA);
  const personBName = getPersonDisplayName(personB);
  const normalized = normalizeRelationshipType(relationshipType);
  const displayRelationshipLabel = toDisplayRelationshipLabel(relationshipType).toLowerCase();

  switch (normalized) {
    case 'parent':
      return `${personBName} is ${getParentLabelForGender(personB?.gender).toLowerCase()} of ${personAName}`;
    case 'stepfather':
    case 'stepmother':
    case 'child':
    case 'grandparent':
    case 'grandchild':
    case 'aunt':
    case 'uncle':
      return `${personBName} is ${displayRelationshipLabel} of ${personAName}`;
    case 'inlaw':
    case 'stepsibling':
      return `${personAName} is ${displayRelationshipLabel} of ${personBName}`;
    default:
      return `${personAName} is ${displayRelationshipLabel} of ${personBName}`;
  }
};

export const getCanvasEdgeSemantics = (
  relationshipType: string | undefined,
  personB?: Pick<PersonDto, 'gender'> | null
) => {
  const normalized = normalizeRelationshipType(relationshipType);

  if (normalized === 'sibling') {
    return {
      label: 'Sibling',
      sourceArrowShape: 'none',
      targetArrowShape: 'none',
      lineStyle: 'solid',
    } as const;
  }

  if (normalized === 'stepsibling') {
    return {
      label: 'Step-Sibling',
      sourceArrowShape: 'none',
      targetArrowShape: 'none',
      lineStyle: 'dashed',
    } as const;
  }

  if (normalized === 'spouse') {
    return {
      label: getDisplayRelationshipLabelForEdge(relationshipType, personB),
      sourceArrowShape: 'triangle',
      targetArrowShape: 'triangle',
      lineStyle: 'solid',
    } as const;
  }

  return {
    label: getDisplayRelationshipLabelForEdge(relationshipType, personB),
    sourceArrowShape: 'none',
    targetArrowShape: 'triangle',
    lineStyle: 'solid',
  } as const;
};
